"use client"

import { useEffect, useState } from "react"
import type { Dispatch, SetStateAction } from "react"
import type { BooruPost } from "@/lib/booru-api"

// Moved out of components/booru-detail-modal.tsx (same state, same effects in the same order, same dependencies).
export function useModalAudio({
  post,
  initialVolume,
  isSlideshowActive,
  setIsSlideshowActive,
  canNavigate,
  onNavigate,
}: {
  post: BooruPost | null
  initialVolume: number
  isSlideshowActive: boolean
  setIsSlideshowActive: Dispatch<SetStateAction<boolean>>
  canNavigate?: { prev: boolean; next: boolean }
  onNavigate?: (direction: "prev" | "next") => void
}) {
  // Audio state for Doujins.com audio doujins
  const [audioManager, setAudioManager] = useState<any>(null)
  const [isAudioMode, setIsAudioMode] = useState(false)
  const [audioVolume, setAudioVolume] = useState(initialVolume)
  const [isAudioMuted, setIsAudioMuted] = useState(false)
  const [audioProgress, setAudioProgress] = useState(0)
  const [hasAudio, setHasAudio] = useState(false)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)

  // Initialize AudioManager
  useEffect(() => {
    if (typeof window === 'undefined') return

    import('@/lib/audio-manager').then(({ AudioManager }) => {
      const manager = new AudioManager()
      setAudioManager(manager)
      setAudioVolume(manager.getVolume())
      setIsAudioMuted(manager.getMuted())

      return () => {
        manager.cleanup()
      }
    })
  }, [])

  // Detect if current post has audio
  useEffect(() => {
    if (post?.rawPost?.hasAudio || post?.rawPost?.audioUrl) {
      setHasAudio(true)
      setIsAudioMode(true) // Auto-enable audio mode for audio doujins
    } else {
      setHasAudio(false)
      setIsAudioMode(false)
    }
  }, [post])

  // Audio playback effect
  useEffect(() => {
    if (!audioManager || !isAudioMode || !hasAudio || !post?.rawPost?.audioUrl) return

    const playAudio = async () => {
      try {
        await audioManager.playAudio(post.rawPost.audioUrl)
        setIsAudioPlaying(true)
        console.log('[Audio] Playing audio for post:', post.id)
      } catch (error) {
        console.error('[Audio] Failed to play audio:', error)
        setIsAudioPlaying(false)
      }
    }

    playAudio()

    return () => {
      if (audioManager) {
        audioManager.stop()
        setIsAudioPlaying(false)
      }
    }
  }, [post, isAudioMode, hasAudio, audioManager])

  // Audio-synced slideshow
  useEffect(() => {
    if (!audioManager || !isSlideshowActive || !isAudioMode || !hasAudio) return

    const handleAudioEnded = () => {
      console.log('[Audio] Audio ended, advancing to next')
      if (canNavigate?.next) {
        onNavigate?.('next')
      } else {
        setIsSlideshowActive(false)
      }
    }

    audioManager.onEnded(handleAudioEnded)

    return () => {
      if (audioManager) {
        audioManager.onEnded(() => {})
      }
    }
  }, [audioManager, isSlideshowActive, isAudioMode, hasAudio, canNavigate, onNavigate])

  // Audio progress tracking — use native timeupdate (~4 Hz) instead of a 100ms interval
  useEffect(() => {
    if (!audioManager || !isAudioMode || !hasAudio) return

    const el = audioManager.getAudioElement()
    const onTimeUpdate = () => {
      const duration = el.duration
      if (duration > 0) setAudioProgress((el.currentTime / duration) * 100)
    }
    el.addEventListener("timeupdate", onTimeUpdate)
    return () => el.removeEventListener("timeupdate", onTimeUpdate)
  }, [audioManager, isAudioMode, hasAudio])

  return {
    audioManager, isAudioMode, setIsAudioMode, audioVolume, setAudioVolume, isAudioMuted, setIsAudioMuted,
    audioProgress, hasAudio, isAudioPlaying, setIsAudioPlaying,
  }
}
